import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Fingerprint, CheckCircle2, LogOut, ScanLine, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { cn, formatApiError } from '../lib/utils';
import fingerprintService, { findBestMatch } from '../lib/fingerprintService';
import logger from '../lib/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const ROLE_LABEL = { admin: 'Administrador', barber: 'Barbero', stylist: 'Estilista' };

const fmtTime = (iso) =>
  new Date(iso).toLocaleTimeString('es-MX', {
    hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'America/Mexico_City',
  });

const fmtDuration = (min) => {
  if (min == null) return '';
  const h = Math.floor(min / 60);
  const m = min % 60;
  return `${h}h ${String(m).padStart(2, '0')}min`;
};

export default function ChecadorPage() {
  const navigate = useNavigate();
  const { selectedBusiness } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [status, setStatus] = useState('idle'); // idle | scanning | result | error
  const [result, setResult] = useState(null);
  const [sdkReady, setSdkReady] = useState(false);
  const [now, setNow] = useState(new Date());
  const resetTimer = useRef(null);

  const loadTemplates = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/fingerprints/templates`);
      setTemplates(data || []);
    } catch (err) {
      toast.error('Error al cargar plantillas');
      logger.error('load templates', err);
    }
  }, []);

  useEffect(() => {
    loadTemplates();
    const clock = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(clock);
  }, [loadTemplates]);

  const resetSoon = (ms = 3500) => {
    if (resetTimer.current) clearTimeout(resetTimer.current);
    resetTimer.current = setTimeout(() => {
      setStatus('idle');
      setResult(null);
      startScan();
    }, ms);
  };

  const [lastDebug, setLastDebug] = useState(null);

  const doCheck = useCallback(async (userId) => {
    try {
      const { data } = await axios.post(`${API_URL}/attendance/check`, { user_id: userId });
      setResult({ ok: true, ...data });
      setStatus('result');
      resetSoon(4000);
    } catch (err) {
      setResult({ ok: false, message: formatApiError(err, 'Error al registrar') });
      setStatus('error');
      resetSoon(3000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startScan = useCallback(async () => {
    if (!fingerprintService.isSdkLoaded()) {
      setStatus('error');
      setResult({ ok: false, message: 'WebSDK no cargado. Ejecuta el Lite Client.' });
      return;
    }
    try {
      await fingerprintService.init();
      setSdkReady(true);
      setStatus('scanning');
      await fingerprintService.start();
    } catch (err) {
      setStatus('error');
      setResult({ ok: false, message: err.message || 'No se pudo iniciar el lector' });
    }
  }, []);

  // Wire the sample event once
  useEffect(() => {
    const off = fingerprintService.on('sample', async ({ template_b64 }) => {
      // Stop scanner while we process the match
      await fingerprintService.stop();
      const { match, debug } = findBestMatch(template_b64, templates);
      setLastDebug(debug);
      if (!match) {
        setStatus('error');
        const bestScoreTxt = debug ? ` (mejor score: ${debug.bestScore.toFixed(3)} / mín ${debug.threshold})` : '';
        setResult({ ok: false, message: `Huella no reconocida${bestScoreTxt}. Asegúrate de enrolar la huella primero.` });
        resetSoon(3500);
        return;
      }
      await doCheck(match.user_id);
    });
    const offErr = fingerprintService.on('error', ({ message }) => {
      setStatus('error');
      setResult({ ok: false, message: message || 'Error del lector' });
      resetSoon(3000);
    });
    return () => { off(); offErr(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templates]);

  // Kick off scanning when the page loads and templates are ready
  useEffect(() => {
    if (templates.length > 0 && status === 'idle') startScan();
    return () => { fingerprintService.stop(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templates.length]);

  const exit = async () => {
    await fingerprintService.stop();
    if (resetTimer.current) clearTimeout(resetTimer.current);
    navigate('/asistencia');
  };

  return (
    <div className="fixed inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-emerald-900 flex flex-col items-center justify-center overflow-hidden" data-testid="checador-page">
      {/* Top bar */}
      <div className="absolute top-0 left-0 right-0 px-8 py-6 flex items-center justify-between text-white/80">
        <div>
          <p className="text-sm opacity-70">{selectedBusiness?.name}</p>
          <p className="text-3xl font-bold tabular-nums" style={{ fontFamily: 'Manrope' }}>
            {now.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false, timeZone: 'America/Mexico_City' })}
          </p>
          <p className="text-sm opacity-70">
            {now.toLocaleDateString('es-MX', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', timeZone: 'America/Mexico_City' })}
          </p>
        </div>
        <Button
          variant="ghost"
          className="text-white/70 hover:text-white hover:bg-white/10"
          onClick={exit}
          data-testid="exit-kiosk"
        >
          <LogOut className="w-4 h-4 mr-2" /> Salir
        </Button>
      </div>

      {/* Center content */}
      <div className="flex-1 flex flex-col items-center justify-center px-8">
        {status === 'idle' || status === 'scanning' ? (
          <div className="text-center animate-fade-in">
            <div className={cn(
              'w-48 h-48 rounded-full flex items-center justify-center mb-8 mx-auto',
              'bg-white/10 backdrop-blur-sm border-4 border-white/30',
              status === 'scanning' && 'animate-pulse',
            )}>
              <Fingerprint className="w-24 h-24 text-white" />
            </div>
            <h1 className="text-5xl font-bold text-white mb-3" style={{ fontFamily: 'Manrope' }}>
              Coloca tu dedo
            </h1>
            <p className="text-xl text-white/70">
              {templates.length > 0
                ? `${templates.length} miembro(s) del personal enrolado(s)`
                : 'Sin enrolamientos — pide al administrador que enrole a alguien'}
            </p>
            {!sdkReady && (
              <div className="mt-6 inline-flex items-center gap-2 text-amber-200 bg-amber-900/40 px-4 py-2 rounded-full">
                <ScanLine className="w-4 h-4" /> Verificando conexión con el lector…
              </div>
            )}
          </div>
        ) : status === 'result' ? (
          <div className="text-center animate-fade-in" data-testid="checador-result">
            <div className="w-48 h-48 rounded-full flex items-center justify-center mb-8 mx-auto bg-emerald-400/20 backdrop-blur border-4 border-emerald-400">
              <CheckCircle2 className="w-24 h-24 text-emerald-300" />
            </div>
            <p className="text-sm uppercase tracking-widest text-emerald-300 mb-2">
              {result.action === 'check_in' ? 'ENTRADA' : 'SALIDA'} REGISTRADA
            </p>
            <h1 className="text-6xl font-bold text-white mb-2" style={{ fontFamily: 'Manrope' }}>
              {result.action === 'check_in' ? '¡Bienvenido!' : '¡Hasta luego!'}
            </h1>
            <p className="text-3xl text-white/90 mb-1">{result.user_name}</p>
            <p className="text-sm text-white/50 mb-6">{ROLE_LABEL[result.user_role] || result.user_role}</p>
            <div className="inline-flex items-center gap-6 text-white/80 bg-white/5 rounded-2xl px-8 py-4">
              <div>
                <p className="text-xs opacity-60">Hora</p>
                <p className="text-2xl font-mono">{fmtTime(result.time)}</p>
              </div>
              {result.scheduled_start && (
                <div>
                  <p className="text-xs opacity-60">Programado</p>
                  <p className="text-lg font-mono">{result.scheduled_start} – {result.scheduled_end}</p>
                </div>
              )}
              {result.worked_minutes != null && (
                <div>
                  <p className="text-xs opacity-60">Trabajado hoy</p>
                  <p className="text-lg font-semibold">{fmtDuration(result.worked_minutes)}</p>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="text-center animate-fade-in" data-testid="checador-error">
            <div className="w-48 h-48 rounded-full flex items-center justify-center mb-8 mx-auto bg-red-400/20 backdrop-blur border-4 border-red-400">
              <XCircle className="w-24 h-24 text-red-300" />
            </div>
            <h1 className="text-4xl font-bold text-white mb-3" style={{ fontFamily: 'Manrope' }}>Intenta de nuevo</h1>
            <p className="text-lg text-white/70 max-w-md mx-auto">{result?.message}</p>
          </div>
        )}
      </div>

      <p className="absolute bottom-6 text-white/40 text-xs">
        Presiona &quot;Salir&quot; para regresar al panel de administración
      </p>
      {lastDebug && (
        <div className="absolute bottom-16 left-1/2 -translate-x-1/2 max-w-md text-center">
          <details className="text-[10px] text-white/40">
            <summary className="cursor-pointer">Diagnóstico matcher</summary>
            <div className="mt-1 font-mono text-left bg-black/40 p-2 rounded">
              <div>umbral: {lastDebug.threshold}</div>
              <div>mejor score: {lastDebug.bestScore?.toFixed(3)}</div>
              {(lastDebug.allScores || []).slice(0, 6).map((s) => (
                <div key={s.user_id}>{s.user_name}: {s.score.toFixed(3)}</div>
              ))}
            </div>
          </details>
        </div>
      )}
    </div>
  );
}
